import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { API_DOCS } from './OrdiniFornitoriPage.api';
import './OrdiniFornitoriPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoOrdineFornitore = 'BOZZA' | 'EMESSO' | 'CHIUSO' | 'ANNULLATO';

interface OrdineFornitoreItem {
  id: number;
  societaId: number;
  sedeId: number;
  fornitoreId: number;
  stato: StatoOrdineFornitore;
  numero: string | null;
  dataEmissione: string | null;
  fornitoreRagioneSociale: string | null;
  note: string | null;
  createdAt: string;
  fornitore: { id: number; ragioneSociale: string } | null;
  sede: { id: number; nome: string } | null;
  _count: { righe: number };
  // Merce ricevuta coi carichi di magazzino collegati
  ricezione: 'NESSUNA' | 'PARZIALE' | 'COMPLETA';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_ORDINE: Record<StatoOrdineFornitore, { label: string; cls: string }> = {
  BOZZA:     { label: 'Bozza',     cls: 'bop-badge-neutral' },
  EMESSO:    { label: 'Emesso',    cls: 'bop-badge-success' },
  CHIUSO:    { label: 'Chiuso',    cls: 'bop-badge-info' },
  ANNULLATO: { label: 'Annullato', cls: 'bop-badge-danger' },
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function renderStato(stato: StatoOrdineFornitore): React.ReactNode {
  const cfg = STATO_ORDINE[stato] ?? { label: stato, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function OrdiniFornitoriPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [stato, setStato] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<OrdineFornitoreItem[]>([]);
  const [loading, setLoading] = useState(false);

  const canEdit = can('acquisti.ordini_fornitori', 'edit');

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (stato) params.set('stato', stato);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/ordini-fornitori?societaId&stato -> {"success":true,"data":[{"id":"number","societaId":"number","sedeId":"number","fornitoreId":"number","stato":"BOZZA|EMESSO|CHIUSO|ANNULLATO","numero":"string|null","dataEmissione":"string|null","fornitoreRagioneSociale":"string|null","note":"string|null","createdAt":"string","fornitore":{"id":"number","ragioneSociale":"string"},"sede":{"id":"number","nome":"string"},"_count":{"righe":"number"},"ricezione":"NESSUNA|PARZIALE|COMPLETA"}]}
      const r = await apiFetch<{ success: boolean; data: OrdineFornitoreItem[] }>(`/api/ordini-fornitori${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento degli ordini fornitore'), 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, stato]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Ricerca client-side su numero, fornitore e sede
  const itemsFiltrati = useMemo(() => {
    if (!q) return items;
    return items.filter(o => {
      const testo = [
        o.numero || '',
        o.fornitore?.ragioneSociale || o.fornitoreRagioneSociale || '',
        o.sede?.nome || '',
      ].join(' ').toLowerCase();
      return testo.includes(q);
    });
  }, [items, q]);

  const pulisciFiltri = () => {
    setStato('');
    setQInput('');
    setQ('');
  };

  return (
    <Layout>
      <div className="odf-lista">
        {/* Header */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Ordini Fornitori</h1>
          {canEdit && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => navigate('/commerciale/passivo/ordini/nuovi')}
            >
              + Componi ordini
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar odf-lista__filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input odf-lista__search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, fornitore, sede..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select
              className="bop-select"
              value={stato}
              onChange={e => setStato(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="BOZZA">Bozza</option>
              <option value="EMESSO">Emesso</option>
              <option value="CHIUSO">Chiuso</option>
              <option value="ANNULLATO">Annullato</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare gli ordini fornitore</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Fornitore</th>
                  <th>Sede</th>
                  <th className="bop-text-right">Righe</th>
                  <th>Stato</th>
                  <th>Data emissione</th>
                  <th>Creato il</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : itemsFiltrati.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">
                          Nessun ordine fornitore trovato con i filtri attuali.
                          <br />
                          Gli ordini fornitore si compongono dal wizard partendo dalle RDA aperte.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemsFiltrati.map(o => (
                    <tr key={o.id} onClick={() => navigate(`/commerciale/passivo/ordini/${o.id}`)}>
                      <td>
                        {o.numero ? (
                          <span className="odf-lista__numero">{o.numero}</span>
                        ) : (
                          <span className="bop-badge bop-badge-neutral">Bozza</span>
                        )}
                      </td>
                      <td>{o.fornitore?.ragioneSociale || o.fornitoreRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                      <td>{o.sede?.nome || <span className="bop-text-muted">—</span>}</td>
                      <td className="bop-text-right">{o._count?.righe ?? 0}</td>
                      <td>
                        {renderStato(o.stato)}{' '}
                        {o.stato === 'EMESSO' && o.ricezione === 'PARZIALE' && (
                          <span className="bop-badge bop-badge-warning" title="Parte della merce è già stata caricata in magazzino">
                            Ricevuto in parte
                          </span>
                        )}
                      </td>
                      <td>{formatDateIT(o.dataEmissione)}</td>
                      <td>{formatDateIT(o.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
